import { motion } from 'framer-motion';

/**
 * CloudHubIA, native SVG rendering of the CloudHub information architecture.
 * Replaces an earlier image screenshot so the diagram is crisp at every DPI,
 * theme-aware, and lazy-loads without an image fetch.
 *
 * Tree structure (from the source case-study asset):
 *   Cloud Hub
 *   └─ Login
 *      └─ Cloud Hub Landing page
 *         ├─ Cloud Dashboard → AWS Dashboard, Azure Dashboard
 *         ├─ My Cloud Space → Overview, Servers, Object Store, Database
 *         ├─ Falcon Scheduler → Overview, Servers, Object Store, Database
 *         ├─ Gamification Portal → Overview, Inventory statistics
 *         ├─ Self Service Portal → Overview, Order Processing, Order Details
 *         ├─ Release Schedule → Information Hub → Cloud Tools → Cloud Services → Cloud Dashboards
 *         └─ Falcon Admin → Overview, Accounts, Tags, Manage
 *
 * Two node styles:
 *, "primary"  (bright blue)  : parent / branching modules
 *, "leaf"     (soft mint)    : terminal child pages
 *, "root"     (deep navy)    : the Cloud Hub root
 *
 * The whole thing lives inside a single 1400×900 SVG viewBox so it scales
 * fluidly. Boxes are 140×28 with 4px corners, connectors are 1.5px strokes.
 */

const W = 1400;
const H = 900;
const BOX_W = 148;
const BOX_H = 30;
const R = 5;

const NODE_STYLES = {
    root:    { fill: '#0f1e3d', text: '#f8fafc', stroke: '#0a1428' },
    primary: { fill: '#3b82f6', text: '#ffffff', stroke: '#2563eb' },
    leaf:    { fill: '#c5e8e2', text: '#0f172a', stroke: '#a5d5cd' },
};

// Each node: { id, label, x (center), y (top), style }
const nodes = [
    // Trunk
    { id: 'cloudhub',      label: 'Cloud Hub',             cx: 700, y: 40, style: 'root'    },
    { id: 'login',         label: 'Login',                 cx: 700, y: 105, style: 'leaf'    },
    { id: 'landing',       label: 'Cloud Hub Landing page', cx: 700, y: 170, style: 'primary' },

    // Level-3 modules (left → right)
    { id: 'dashboard',     label: 'Cloud Dashboard',       cx: 130, y: 260, style: 'primary' },
    { id: 'cloudspace',    label: 'My Cloud Space',        cx: 300, y: 260, style: 'primary' },
    { id: 'scheduler',     label: 'Falcon Scheduler',      cx: 470, y: 260, style: 'primary' },
    { id: 'gamification',  label: 'Gamification Portal',   cx: 640, y: 260, style: 'primary' },
    { id: 'selfservice',   label: 'Self Service Portal',   cx: 810, y: 260, style: 'primary' },
    { id: 'release',       label: 'Release Schedule',      cx: 980, y: 260, style: 'primary' },
    { id: 'falconadmin',   label: 'Falcon Admin',          cx: 1230, y: 260, style: 'primary' },

    // Children, Cloud Dashboard
    { id: 'aws',           label: 'AWS Dashboard',         cx: 130, y: 350, style: 'leaf'    },
    { id: 'azure',         label: 'Azure Dashboard',       cx: 130, y: 400, style: 'leaf'    },

    // Children, My Cloud Space
    { id: 'cs-overview',   label: 'Overview',              cx: 300, y: 350, style: 'leaf'    },
    { id: 'cs-servers',    label: 'Servers',               cx: 300, y: 400, style: 'leaf'    },
    { id: 'cs-object',     label: 'Object Store',          cx: 300, y: 450, style: 'leaf'    },
    { id: 'cs-db',         label: 'Database',              cx: 300, y: 500, style: 'leaf'    },

    // Children, Falcon Scheduler
    { id: 'sc-overview',   label: 'Overview',              cx: 470, y: 350, style: 'leaf'    },
    { id: 'sc-servers',    label: 'Servers',               cx: 470, y: 400, style: 'leaf'    },
    { id: 'sc-object',     label: 'Object Store',          cx: 470, y: 450, style: 'leaf'    },
    { id: 'sc-db',         label: 'Database',              cx: 470, y: 500, style: 'leaf'    },

    // Children, Gamification
    { id: 'g-overview',    label: 'Overview',              cx: 640, y: 350, style: 'leaf'    },
    { id: 'g-inv',         label: 'Inventory statistics',  cx: 640, y: 400, style: 'leaf'    },

    // Children, Self Service
    { id: 'ss-overview',   label: 'Overview',              cx: 810, y: 350, style: 'leaf'    },
    { id: 'ss-order',      label: 'Order Processing',      cx: 810, y: 400, style: 'leaf'    },
    { id: 'ss-details',    label: 'Order Details',         cx: 810, y: 450, style: 'leaf'    },

    // Children, Release Schedule (chain, all primary blue)
    { id: 'r-hub',         label: 'Information Hub',       cx: 980, y: 350, style: 'primary' },
    { id: 'r-tools',       label: 'Cloud Tools',           cx: 980, y: 400, style: 'primary' },
    { id: 'r-services',    label: 'Cloud Services',        cx: 980, y: 450, style: 'primary' },
    { id: 'r-dash',        label: 'Cloud Dashboards',      cx: 980, y: 500, style: 'primary' },

    // Children, Falcon Admin
    { id: 'fa-overview',   label: 'Overview',              cx: 1230, y: 350, style: 'leaf'    },
    { id: 'fa-accounts',   label: 'Accounts',              cx: 1230, y: 400, style: 'leaf'    },
    { id: 'fa-tags',       label: 'Tags',                  cx: 1230, y: 450, style: 'leaf'    },
    { id: 'fa-manage',     label: 'Manage',                cx: 1230, y: 500, style: 'leaf'    },
];

// Parent → children edges (drawn as elbow connectors below).
const edges = [
    ['cloudhub', 'login'],
    ['login', 'landing'],
    // Landing branches to all 7 modules
    ['landing', 'dashboard'],
    ['landing', 'cloudspace'],
    ['landing', 'scheduler'],
    ['landing', 'gamification'],
    ['landing', 'selfservice'],
    ['landing', 'release'],
    ['landing', 'falconadmin'],
    // Cloud Dashboard children
    ['dashboard', 'aws'],
    ['aws', 'azure'],
    // Cloud Space children
    ['cloudspace', 'cs-overview'],
    ['cs-overview', 'cs-servers'],
    ['cs-servers', 'cs-object'],
    ['cs-object', 'cs-db'],
    // Scheduler children
    ['scheduler', 'sc-overview'],
    ['sc-overview', 'sc-servers'],
    ['sc-servers', 'sc-object'],
    ['sc-object', 'sc-db'],
    // Gamification children
    ['gamification', 'g-overview'],
    ['g-overview', 'g-inv'],
    // Self-service children
    ['selfservice', 'ss-overview'],
    ['ss-overview', 'ss-order'],
    ['ss-order', 'ss-details'],
    // Release chain
    ['release', 'r-hub'],
    ['r-hub', 'r-tools'],
    ['r-tools', 'r-services'],
    ['r-services', 'r-dash'],
    // Falcon Admin children
    ['falconadmin', 'fa-overview'],
    ['fa-overview', 'fa-accounts'],
    ['fa-accounts', 'fa-tags'],
    ['fa-tags', 'fa-manage'],
];

const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));

// Draw a connector between two nodes as an elbow path.
// If both nodes share the same cx, use a straight vertical.
// If different cx (branching from a parent to a horizontally-offset child),
// draw down half the gap, over, then down again.
const connectorPath = (from, to) => {
    const fromX = from.cx;
    const fromY = from.y + BOX_H; // bottom of parent
    const toX = to.cx;
    const toY = to.y; // top of child

    if (fromX === toX) {
        return `M ${fromX} ${fromY} L ${toX} ${toY}`;
    }
    // Elbow: down to midY, horizontal, then down
    const midY = fromY + (toY - fromY) * 0.55;
    return `M ${fromX} ${fromY} L ${fromX} ${midY} L ${toX} ${midY} L ${toX} ${toY}`;
};

export const CloudHubIA = () => (
    <motion.figure
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-white p-4 md:p-6 overflow-hidden"
        data-testid="cloudhub-ia-diagram"
    >
        <svg
            viewBox={`0 0 ${W} ${H - 350}`}
            className="block w-full h-auto"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="CloudHub information architecture diagram, 4-level hierarchy"
        >
            {/* subtle background wash for reference */}
            <rect width={W} height={H - 350} fill="#f8fafc" />

            {/* Connectors first so nodes overlay them */}
            <g fill="none" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round">
                {edges.map(([fromId, toId], i) => {
                    const from = byId[fromId];
                    const to = byId[toId];
                    if (!from || !to) return null;
                    return (
                        <motion.path
                            key={i}
                            d={connectorPath(from, to)}
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            viewport={{ once: true, margin: '-6%' }}
                            transition={{ delay: 0.15 + i * 0.015, duration: 0.5, ease: 'easeOut' }}
                        />
                    );
                })}
            </g>

            {/* Nodes */}
            <g>
                {nodes.map((n, i) => {
                    const style = NODE_STYLES[n.style];
                    return (
                        <motion.g
                            key={n.id}
                            initial={{ opacity: 0, y: 6 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-6%' }}
                            transition={{ delay: 0.1 + i * 0.02, duration: 0.4, ease: 'easeOut' }}
                        >
                            <rect
                                x={n.cx - BOX_W / 2}
                                y={n.y}
                                width={BOX_W}
                                height={BOX_H}
                                rx={R}
                                ry={R}
                                fill={style.fill}
                                stroke={style.stroke}
                                strokeWidth="0.5"
                            />
                            <text
                                x={n.cx}
                                y={n.y + BOX_H / 2}
                                fill={style.text}
                                fontSize="12.5"
                                fontFamily="ui-sans-serif, system-ui, sans-serif"
                                fontWeight={n.style === 'leaf' ? 500 : 600}
                                textAnchor="middle"
                                dominantBaseline="central"
                            >
                                {n.label}
                            </text>
                        </motion.g>
                    );
                })}
            </g>
        </svg>
        <figcaption className="mt-4 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
            Simplified information architecture, 4-level hierarchy · rendered natively
        </figcaption>
    </motion.figure>
);

export default CloudHubIA;
